<template>
  <v-container class="max-w-4xl mx-auto p-6">
    <h1 class="text-3xl font-bold mb-6">User Guide and Workflow</h1>
    <p class="text-gray-700 mb-4">
      The Lightning Model Page is a configurable solution designed for websites that display model
      details. Each website leveraging this page will have a dedicated Admin Dashboard for
      management and customization. Below is an organized guide explaining its functionality and how
      to use it effectively:
    </p>

    <v-expansion-panels>
      <!-- Admin Overview -->
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold">Admin Overview</v-expansion-panel-title>
        <v-expansion-panel-text>
          <section class="my-4">
            <p class="text-gray-700 mb-4">
              The Admin Dashboard is the central hub for configuring and managing the Lightning
              Model Page. It offers the following key capabilities:
            </p>
            <ul class="my-2 mx-8">
              <li>
                <strong>Pages</strong>
                <ul class="mx-8">
                  <li>Page Analytics and Performance Dashboard</li>
                  <li>Page Management</li>
                </ul>
              </li>
              <li>
                <strong>Trends</strong>
                <ul class="mx-8">
                  <li>Metrics Dashboard</li>
                </ul>
              </li>
              <li>
                <strong>Global Settings</strong>
                <ul class="mx-8">
                  <li>Primary CTA Customization</li>
                  <li>Secondary CTA Customization</li>
                  <li>Trim Card Customization</li>
                </ul>
              </li>
              <li>
                <strong>Help</strong>
              </li>
            </ul>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Page Management -->
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold">Page Management</v-expansion-panel-title>
        <v-expansion-panel-text>
          <section class="my-4">
            <p class="text-gray-700 mb-4">
              The dashboard allows you to create and manage pages on the website.
            </p>
            <ul class="my-2 mx-8">
              <li>
                <strong>Adding Pages:</strong>
                <ul class="mx-8">
                  <li>Create a new page for display on or off your website.</li>
                  <li>Define specific configurations for each page based on the goals.</li>
                </ul>
              </li>
              <li>
                <strong>Listings Display:</strong>
                <ul class="mx-8">
                  <li>Each page will showcase model details</li>
                  <li>
                    Clicking on a CTA button records an interaction, contributing to overall
                    analytics.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Page Overview:</strong>
                <ul class="mx-8">
                  <li>
                    After customizing and saving a page to your satisfaction, it will appear in the
                    <strong>Pages Tab.</strong>
                  </li>
                  <li>
                    Each page will have an option to "Copy Embed Code," which generates a script.
                    Use the script directly into your website to deploy the page. Once embedded, the
                    page will be live and functional.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Page Customization:</strong>
                <ul class="mx-8">
                  <li>
                    <strong>Highlighted Features:</strong> select the features you want to display
                    on the page.
                  </li>
                  <li>Customize highlighted features section style</li>
                </ul>
              </li>
            </ul>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Analytics and Performance Tracking -->
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold"
          >Analytics and Performance Tracking</v-expansion-panel-title
        >
        <v-expansion-panel-text>
          <section class="my-4">
            <p class="text-gray-700 mb-4">
              The analytics and performance dashboards provide usage statistics to monitor the
              performance of the Lightning Model Page. Key metrics include:
            </p>
            <ul class="my-2 mx-8">
              <li>Track the total number of pages added for the website.</li>
              <li>
                <strong>Click Statistics:</strong>
                <ul class="mx-8">
                  <li>Tracks the total number of clicks for each page.</li>
                  <li>Displays the Clickthrough Rate (CTR) as a percentage.</li>
                </ul>
              </li>
              <li>
                <strong>Load Metrics:</strong>
                <ul class="mx-8">
                  <li>Measures the number of loads for each page.</li>
                  <li>Displays load percentages for performance insights.</li>
                </ul>
              </li>
              <li>
                <strong>Filters and Comparisons:</strong>
                <ul class="mx-8">
                  <li>Apply date filters to view performance over specific time periods.</li>
                  <li>Compare data across different time frames for trends and insights.</li>
                </ul>
              </li>
            </ul>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Workflow for Customizing and Integrating Lightning Model Page -->
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold"
          >Workflow for Customizing and Integrating Lightning Model Page</v-expansion-panel-title
        >
        <v-expansion-panel-text>
          <section class="my-4">
            <ol class="my-2 mx-8">
              <li>
                <strong>Access the Admin Dashboard:</strong>
                <ul class="mx-8">
                  <li>
                    Log in to the Admin Dashboard associated with your website(each website has a
                    specific account_id).
                  </li>
                </ul>
              </li>
              <li>
                <strong>Create or Edit a Page:</strong>
                <ul class="mx-8">
                  <li>Navigate to the pages section.</li>
                  <li>
                    Add a new page or edit an existing one with specific customizations (e.g.,
                    highlighted features).
                  </li>
                </ul>
              </li>
              <li>
                <strong>Save and Deploy the Page:</strong>
                <ul class="mx-8">
                  <li>Save your customizations and make the page perfect for your needs.</li>
                  <li>Go to the <strong>Pages Tab</strong> to see all created pages.</li>
                  <li>
                    Click on a page to access the
                    <strong>"Copy Embed Code"</strong> option.
                  </li>
                  <li>
                    Generate the script and insert it directly into your website to make the page
                    live.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Monitor Performance:</strong>
                <ul class="mx-8">
                  <li>Regularly check the dashboard for updated performance analytics.</li>
                  <li>Use the data to refine configurations and improve user engagement.</li>
                </ul>
              </li>
            </ol>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <section class="mt-8">
        For help, contact
        <a href="mailto:support@jazel.com">support@jazel.com</a> or visit
        <a href="https://www.jazel.com">https://www.jazel.com</a> to chat with us.
      </section>
    </v-expansion-panels>
  </v-container>
</template>

<script setup lang="ts"></script>

<style scoped></style>
